import { AdminTaskStatus } from '@/client/admin-tasks/types';
import { FlexContainer } from '@/ui/styled-ui';
import {
  CompletedTaskIcon,
  SkippedTaskIcon,
  TodoTaskIcon,
} from '@/utils/helpers';
import { Skeleton } from 'primereact/skeleton';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledEmptyState = styled(FlexContainer)`
  min-height: 254px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--big-padding);
  text-align: center;
  background: var(--beige-main);
  border-radius: var(--medium-border-radius);
  border: 1px solid var(--gray-dark);
`;

const StyledTitle = styled.h3<{ isTodo: boolean }>`
  font-size: var(--heading-small-font-size);
  line-height: var(--heading-small-line-height);
  font-weight: 600;
  color: ${({ isTodo }) =>
    isTodo ? 'var(--black-main)' : 'var(--gray-darker)'};
  margin: 0;
  margin-top: var(--medium-padding);
`;

const StyledSubtitle = styled.p`
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  margin: 0;
  max-width: 300px;
  margin-top: var(--xsmall-padding);
`;

interface EmptyStateProps {
  status: AdminTaskStatus;
  isLoading?: boolean;
}

const getEmptyStateContent = (
  status: AdminTaskStatus,
  t: (key: string) => string,
) => {
  switch (status) {
    case AdminTaskStatus.TODO:
      return {
        illustration: <TodoTaskIcon />,
        title: t('admin.tasks.empty.todo.title'),
        subtitle: t('admin.tasks.empty.todo.subtitle'),
      };
    case AdminTaskStatus.COMPLETED:
      return {
        illustration: <CompletedTaskIcon />,
        title: t('admin.tasks.empty.completed.title'),
        subtitle: '',
      };
    case AdminTaskStatus.SKIPPED:
      return {
        illustration: <SkippedTaskIcon />,
        title: t('admin.tasks.empty.skipped.title'),
        subtitle: '',
      };
    default:
      return {
        illustration: '📋',
        title: t('admin.tasks.empty.default.title'),
        subtitle: t('admin.tasks.empty.default.subtitle'),
      };
  }
};

export const EmptyState: React.FC<EmptyStateProps> = ({
  status,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const { illustration, title, subtitle } = getEmptyStateContent(status, t);

  return (
    <StyledEmptyState>
      {isLoading ? (
        <Skeleton height="100%" width="100%" />
      ) : (
        <>
          {illustration}
          <StyledTitle isTodo={status === AdminTaskStatus.TODO}>
            {title}
          </StyledTitle>
          {subtitle && <StyledSubtitle>{subtitle}</StyledSubtitle>}
        </>
      )}
    </StyledEmptyState>
  );
};
